import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Input, Button, message, Tooltip, Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import api from '@services/api';
import useSwr from '@hooks/useSwr';
import Table from '@components/Table';
import AdminActions from '@components/AdminActions';
import { PlusCircleOutlined } from '@ant-design/icons';
import SessionContext from '@providers/Session/SessionContext';
import SelectCustomers from '@components/SelectCustomers';
import useSearchParams from '@hooks/useSearchParams';
import rolesConstants from '@utils/rolesConstants';
import CustomTag from '@components/CustomTag';
import UserViewerModal from './UserViewerModal';
import { IUsersData } from '../types';

import * as S from './styles';

const { TabPane } = Tabs;
const { Search } = Input;

const UserAccounts: React.FC = () => {
  const { keycloak } = useContext(KeycloakContext);
  const userAdminLogged = keycloak.idTokenParsed?.sub;
  const {
    isRoot,
    effectiveRoles,
    hasCustomersRoles,
    userLogged,
    isParentCustomer,
    hasOnlyMotBusiness,
  } = useContext(SessionContext);
  const rolesToAccess = effectiveRoles?.portal;
  const isSuperBroker = userLogged?.customerType === 'SUPERBROKER';
  const showTabs = isParentCustomer && (isRoot || !isSuperBroker);

  const isCustomerCanManage = rolesToAccess?.includes(
    rolesConstants.MANAGE_USERS,
  );

  const { t } = useTranslation(['users', 'attributes']);

  const [modalVisible, setModalVisible] = useState(false);
  const [userId, setUserId] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const {
    setSearchParams,
    hasValuesInExtraParams,
    clearSearchParams,
    searchParams,
  } = useSearchParams({
    type: 'pagination',
    defaultParams: ['activeTabPane'],
    extraParams: ['userSearch', 'customerId'],
  });

  const { activeTabPane, userSearch, customerId, page, linesPerPage } = useMemo(
    () => searchParams,
    [searchParams],
  );

  useEffect(() => {
    setSearchValue(userSearch);
  }, [userSearch]);

  const { data: users, mutate: mutateUsers } = useSwr<IUsersData>('/users/', {
    params: {
      filter: activeTabPane === 'customers-users' ? 'CUSTOMERS' : 'MINE',
      page: page ? Number(page) - 1 : 0,
      linesPerPage: linesPerPage ?? 20,
      customerId,
      search: userSearch,
    },
  });

  const deleteUser = useCallback(
    (id: string) => api.delete(`/users/${id}`),
    [],
  );

  const handleMutateUsers = useCallback(
    (type: 'CREATE' | 'UPDATE' | 'DELETE', data: any) => {
      if (users) {
        if (type === 'CREATE') {
          mutateUsers({
            ...users,
            content: [data, ...users.content],
          });
        } else if (type === 'UPDATE') {
          mutateUsers({
            ...users,
            content: users.content.map(user =>
              user.id === data.id ? data : user,
            ),
          });
        } else {
          mutateUsers({
            ...users,
            content: users.content.filter(user => user.id !== data.id) || [],
          });
        }
      }
    },
    [users, mutateUsers],
  );

  const handleActions = useCallback(
    async (actionType: 'UPDATE' | 'DELETE', userIdToDelete: string) => {
      const isUpdateUser = actionType === 'UPDATE';

      if (isUpdateUser) {
        setUserId(userIdToDelete);
        setModalVisible(true);
      } else {
        try {
          await deleteUser(userIdToDelete);
          handleMutateUsers('DELETE', { id: userIdToDelete });
          message.success(t('msgs.success-deleting-user'));
        } catch {
          message.error(t('msgs.error-deleting-user'));
        }
      }
    },
    [deleteUser, t, handleMutateUsers],
  );

  const groupTags = useCallback(
    (value: any) => (
      <div
        style={{
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          wordBreak: 'keep-all',
        }}
      >
        {value.map((tag: any) => (
          <CustomTag key={tag.id + Date.now()} color="blue">
            {`${tag.attributes?.prettyName || tag.prettyName}${
              tag.customerName && activeTabPane === 'customers-users'
                ? ` - ${tag.customerName}`
                : ''
            }`}
          </CustomTag>
        ))}
      </div>
    ),
    [activeTabPane],
  );

  const columns = useMemo(() => {
    const columnsDefault = [
      {
        title: t('table.firstName'),
        dataIndex: 'firstName',
        render: (value: string, record: any) =>
          `${value} ${record?.lastName ?? ''}`.trim(),
      },
      { title: t('table.username'), dataIndex: 'username' },
      {
        title: t('table.profile'),
        dataIndex: 'groups',
        render: groupTags,
      },
      {
        title: t('table.email'),
        dataIndex: 'email',
      },
    ];
    if (isCustomerCanManage) {
      columnsDefault.push({
        title: t('table.actions'),
        dataIndex: 'actions',
        align: 'center' as const,
        // @ts-ignore
        render: (value: any, record: any) => (
          <AdminActions
            isEditDisabled={activeTabPane === 'customers-users'}
            alertDelete={t('modal.delete.alert')}
            entityToExclude={record.firstName}
            ruleToNotExclude={record.id === userAdminLogged}
            onEdit={() => handleActions('UPDATE', record.id)}
            onDelete={() => handleActions('DELETE', record.id)}
          />
        ),
      });
    }

    return columnsDefault;
  }, [
    t,
    groupTags,
    isCustomerCanManage,
    activeTabPane,
    userAdminLogged,
    handleActions,
  ]);

  const onClose = useCallback(() => {
    setModalVisible(false);
    setUserId('');
  }, []);

  const handleTabFilterChange = useCallback(
    (activeKey: string) => {
      clearSearchParams();
      switch (activeKey) {
        case 'my-users':
          setSearchParams({
            customerId: undefined,
            activeTabPane: 'my-users',
          });
          break;
        case 'customers-users':
          setSearchParams({ activeTabPane: 'customers-users' });
          break;
        default:
          break;
      }
    },
    [setSearchParams, clearSearchParams],
  );

  const tableFilters = useMemo(
    () => [
      {
        visible: true,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('name-email'),
        element: (
          <Search
            onSearch={(value: string) => setSearchParams({ userSearch: value })}
            value={searchValue}
            onChange={({ target: { value } }) => setSearchValue(value)}
            allowClear
          />
        ),
      },
      {
        visible: activeTabPane === 'customers-users' && hasCustomersRoles,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('client'),
        element: (
          <SelectCustomers
            value={customerId && Number(customerId)}
            onChange={value => setSearchParams({ customerId: value })}
          />
        ),
      },
    ],
    [
      t,
      searchValue,
      activeTabPane,
      hasCustomersRoles,
      customerId,
      setSearchParams,
    ],
  );

  return (
    <S.UsersContainer>
      <UserViewerModal
        activeTabPane={activeTabPane}
        isMotBusiness={hasOnlyMotBusiness}
        userId={userId}
        visible={modalVisible}
        onClose={onClose}
        mutateUsers={handleMutateUsers}
      />
      {showTabs && (
        <Tabs
          type="card"
          tabBarStyle={{ margin: 0 }}
          defaultActiveKey="my-users"
          onChange={handleTabFilterChange}
          activeKey={activeTabPane}
        >
          <TabPane tab={t('tabs.my-users')} key="my-users" />
          <TabPane
            tab={t(
              `tabs.${hasOnlyMotBusiness ? 'department' : 'customers'}-users`,
            )}
            key="customers-users"
          />
        </Tabs>
      )}
      <Table
        data={users?.content || []}
        columns={columns}
        loading={!users?.content}
        tableKey="users"
        title={t('title')}
        extraActions={
          isCustomerCanManage && (
            <Tooltip
              title={t(
                `actions.${
                  activeTabPane === 'customers-users'
                    ? 'switchContextToCreate'
                    : 'newUser'
                }`,
              )}
            >
              <Button
                icon={<PlusCircleOutlined />}
                onClick={() => setModalVisible(true)}
                disabled={activeTabPane === 'customers-users'}
              />
            </Tooltip>
          )
        }
        filters={{
          list: tableFilters,
          hasValuesInFilters: hasValuesInExtraParams,
          onClearFilters: () => clearSearchParams(),
        }}
        onTableChange={setSearchParams}
        pagination={{ page, linesPerPage, total: users?.totalElements }}
      />
    </S.UsersContainer>
  );
};

export default UserAccounts;
